import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { MdKeyboardArrowUp } from 'react-icons/md';
import apiAdmin from '../../../services/api-admin';
import { FilePath, FormatMoney } from '../../../services/utilities';
import { bootstrap } from '../../../app';
import './merchone.scss';
import MerchOneItemDetail from './merchone-item-detail';
import { useOutletContext } from "react-router";

const MerchOne = () => {
    const { cartItems, setCartItems,  openCart } = useOutletContext();
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [categories, setCategories] = useState([]);
    const [productToView, setProductToView] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedGender, setSelectedGender] = useState('All');
    const [sortBy, setSortBy] = useState('featured');
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
 
    const productViewModalRef = useRef(null);
    const previousCartCountRef = useRef(cartItems.length);
    let { paramMerchOneItemId } = useParams();

    useEffect(() => {
        document.title = 'MerchOne';
    }, [paramMerchOneItemId]);

    useEffect(() => {
        const getProducts = async () => {
            apiAdmin.get(`/get-merchone-products`)
                .then((response) => {
                    setProducts(response.data);
                    if (paramMerchOneItemId) {
                        const productToPreview = response.data.find(p => p.merchOneProductId === Number(paramMerchOneItemId));
                        if (productToPreview) {
                            previewProduct(productToPreview);
                        }
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        getProducts();

        const getProductCategories = async () => {
            apiAdmin.get(`/get-merchone-categories`)
                .then((response) => {
                    setCategories(response.data.map(c => c.categoryName));
                });
        }
        getProductCategories();

        const onScroll = () => {
            setShowBackToTop(window.scrollY > 200);
        };
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);

    }, [paramMerchOneItemId]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearch(search);
        }, 250);

        return () => window.clearTimeout(timeoutId);
    }, [search]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        var mdl = bootstrap.Modal.getInstance(productViewModalRef.current)
        if (mdl) mdl.hide();
        window.history.pushState({}, undefined, "/merchone/")

        setProductToView(null);

        if (cartItems.length > previousCartCountRef.current) {
            openCart();
        }
        previousCartCountRef.current = cartItems.length;
        return undefined;

    }, [cartItems, openCart]);

    const filteredProducts = useMemo(() => {
        let results = products.filter((product) => {
            if(!product.listed) return false;
            const searchTarget = `${product.productNumber} ${product.description}`.toLowerCase();
            return searchTarget.includes(debouncedSearch.trim().toLowerCase());
        });

        if (selectedCategory !== 'All') {
            if (selectedCategory === 'New') {
                results = results.filter((product) => Boolean(product.new));
            } else {
                results = results.filter((product) => product.category === selectedCategory);
            }
        }

        if (selectedGender !== 'All') {
            results = results.filter((product) => product.gender === selectedGender);
        }

        if (sortBy === 'price-low') {
            results = [...results].sort((a, b) => a.price - b.price);
        }

        if (sortBy === 'price-high') {
            results = [...results].sort((a, b) => b.price - a.price);
        }

        if (sortBy === 'name') {
            results = [...results].sort((a, b) => a.description.localeCompare(b.description));
        }

        if (selectedCategory === 'All' && selectedGender === 'All' && sortBy === 'featured' && !debouncedSearch.trim()) {
            results = [...results].sort((a, b) => Number(Boolean(b.new)) - Number(Boolean(a.new)));
        }

        return results;
    }, [debouncedSearch, selectedCategory, selectedGender, sortBy, products]);

    const groupedProducts = useMemo(() => {
        return filteredProducts.reduce((groups, product) => {
            const categoryName = selectedCategory === 'New' ? 'New' : (product.category || 'Other');
            if (!groups[categoryName]) {
                groups[categoryName] = [];
            }
            groups[categoryName].push(product);
            return groups;
        }, {});
    }, [filteredProducts, selectedCategory]);

    const previewProduct = (product) => {
        setProductToView(product);
        var productViewModal = new bootstrap.Modal(productViewModalRef.current, {});
        productViewModal.show();
        window.history.pushState({}, undefined, "/merchone/" + product.merchOneProductId);
    }

    return (
        <>
            <div className="d-flex flex-column gap-2 mb-3">
              
                <div className="row g-2 w-100">
                    <div className="col-12 col-sm-6">
                        <TextField label="Search catalog" value={search} onChange={(event) => setSearch(event.target.value)} fullWidth color="primary" />
                    </div>
                    <div className="col-12 col-sm-2">
                        <FormControl color="primary" fullWidth>
                            <InputLabel id="merchone-category">Category</InputLabel>
                            <Select labelId="merchone-category" value={selectedCategory} label="Category" onChange={(event) => setSelectedCategory(event.target.value)}>
                                <MenuItem key='All' value='All'>All</MenuItem>
                                <MenuItem key='New' value='New'>New</MenuItem>
                                {categories.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <div className="col-12 col-sm-2">
                        <FormControl color="primary" fullWidth>
                            <InputLabel id="merchone-gender">Gender</InputLabel>
                            <Select labelId="merchone-gender" value={selectedGender} label="Gender" onChange={(event) => setSelectedGender(event.target.value)}>
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Men's">Men's</MenuItem>
                                <MenuItem value="Women's">Women's</MenuItem>
                                <MenuItem value="Unisex">Unisex</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className="col-12 col-sm-2">
                        <FormControl color="primary" fullWidth>
                            <InputLabel id="merchone-sort">Sort</InputLabel>
                            <Select labelId="merchone-sort" value={sortBy} label="Sort" onChange={(event) => setSortBy(event.target.value)}>
                                <MenuItem value="featured">Featured</MenuItem>
                                <MenuItem value="price-low">Price: Low to High</MenuItem>
                                <MenuItem value="price-high">Price: High to Low</MenuItem>
                                <MenuItem value="name">Name</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="mb-4">
                    <div className="placeholder-glow mb-3">
                        <span className="placeholder placeholder-lg w-25 bg-light" />
                    </div>
                    <div className="row g-3">
                        {Array.from({ length: 8 }).map((_, index) => (
                            <div key={`skeleton-${index}`} className="col-12 col-sm-6 col-md-3">
                                <div className="card h-100">
                                    <div className="card-body placeholder-glow">
                                        <div className="ratio ratio-4x3 mb-3">
                                            <div className="placeholder bg-light w-100 h-100"></div>
                                        </div>
                                        <div className="placeholder bg-light col-8 d-block"></div>
                                        <div className="placeholder bg-light col-6 d-block mt-2"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                filteredProducts.length === 0 ? 
                    <div className="text-center text-secondary pt-5">
                        No products match the current filter.
                    </div>
                 : 
                    (selectedCategory === 'New' ? ['New'] : categories)
                        .filter((categoryName) => groupedProducts[categoryName]?.length)
                        .map((categoryName) => (
                        <div key={categoryName} className="mb-4">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <h3 className="m-0 text-uppercase border-start border-3 ps-3">
                                    {categoryName}
                                </h3>
                                <div className="text-secondary">{groupedProducts[categoryName].length} items</div>
                            </div>
                            <div className="row g-3">
                                {groupedProducts[categoryName].map((product) => (
                                    <div key={product.merchOneProductId} className="col-12 col-sm-6 col-md-3">
                                        <div className="card w-100 h-100 merchone-card position-relative">
                                            {product.new && <span className="badge position-absolute top-0 start-0 m-2 merchone-new-badge text-uppercase">New!</span>}
                                            <button type="button" onClick={() => { previewProduct(product); }} className="btn p-0 text-start w-100 h-100 border-0 bg-transparent d-flex flex-column">
                                                <div className="ratio ratio-4x3 bg-white">
                                                    <div className="d-flex align-items-center justify-content-center p-3 w-100 h-100">
                                                        <img src={FilePath("/images/merchoneproducts/" + product.thumbnail)} alt={product.description} className="w-100 h-100 object-fit-contain" />
                                                    </div>
                                                </div>
                                                <div className="card-body text-center flex-grow-1">
                                                    <div>
                                                        <span className="fw-bold">{product.description}</span><br />
                                                        <div className="border-top border-2 border-secondary-subtle w-25 mx-auto my-2" />
                                                        <span>{FormatMoney(product.price)}</span>
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                )
            )}

            <button
                type="button"
                aria-label="Back to top"
                inert={!showBackToTop}
                tabIndex={showBackToTop ? 0 : -1}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`btn btn-dark rounded-circle position-fixed bottom-0 end-0 m-3 d-inline-flex align-items-center justify-content-center shadow ${showBackToTop ? 'opacity-100' : 'opacity-0 pe-none'} back-to-top`}
            >
                <MdKeyboardArrowUp size={22} />
            </button>

            <div className="modal shown" tabIndex="-1" id="productViewer" ref={productViewModalRef}>
                <div className="modal-dialog modal-xl modal-fullscreen-lg-down">
                    <div className="modal-content">
                        <MerchOneItemDetail merchOneProduct={productToView} cart={cartItems} setCart={setCartItems} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default MerchOne;
